import qz from 'qz-tray';
import { useCallback } from 'react';
import config from 'src/config';
import { PrintDocType, usePrinterSettingStore } from 'src/store/usePrinterSettingStore';
import { useCommonCodeStore } from 'src/store/useCommonCodeStore';
import {
	LabelPrintData,
	EquipmentLabelPrintData,
	printBulkLabelsAtOnce,
	printEquipmentBulkLabelsAtOnce,
	printToolingBulkLabelsAtOnce,
	shouldSkipLabelPrintInDev,
} from 'src/apis/labelApi';
import { getQzConnected } from 'src/hooks/useQzTray';

export type PrintDataType = 'html' | 'pdf' | 'raw';

export interface QzPrintOptions {
	docType: PrintDocType;
	data: string;
	dataType?: PrintDataType;
	copies?: number;
	labelSize?: { width: number; height: number };
	paperSize?: { width: number; height: number };
}

export const useQzPrint = () => {
	const { getPrinter, getLabelSize } = usePrinterSettingStore();
	const isMediumLabel = useCommonCodeStore(state =>
		state.programList.some(code =>
			code.minorCd === 'LABEEL_PRINT' && code.ref02?.trim().toUpperCase() === 'M'
		)
	);

	const print = useCallback(async (options: QzPrintOptions): Promise<boolean> => {
		const { docType, data, dataType = 'html', copies = 1, labelSize, paperSize } = options;

		if (!qz.websocket.isActive()) {
			if (dataType === 'html') {
				const win = window.open('', '_blank');
				if (win) {
					win.document.write(data);
					win.document.close();
					win.onload = () => { win.print(); win.close(); };
				}
			}
			return true;
		}

		const printerName = getPrinter(docType);
		if (!printerName) {
			// alert(`[${docType}] 프린터가 설정되지 않았습니다.\n설정 > 프린터 설정 메뉴에서 먼저 설정해주세요.`);
			return false;
		}

		try {
			const configOptions: any = {
				copies,
				colorType: 'color',
				duplex: false,
				scaleContent: true,
				rasterize: dataType === 'pdf',
			};

			if (labelSize) {
				configOptions.units = 'mm';
				configOptions.margins = { top: 0, right: 0, bottom: 0, left: 2 };
				configOptions.orientation = 'portrait';
				configOptions.scaleContent = false;
				configOptions.density = 203;
			}

			if (paperSize) {
				configOptions.units = 'mm';
				configOptions.size = paperSize;
			}

			const printConfig = qz.configs.create(printerName, configOptions);

			let printData: any[];
			if (dataType === 'html') {
				printData = [{ type: 'pixel', format: 'html', flavor: 'plain', data }];
			} else if (dataType === 'pdf') {
				printData = [{ type: 'pixel', format: 'pdf', flavor: 'base64', data }];
			} else {
				printData = [{ type: 'raw', format: 'plain', flavor: 'plain', data }];
			}

			await qz.print(printConfig, printData);
			return true;
		} catch (err: any) {
			console.error('출력 실패:', err);
			alert(`출력 실패: ${err?.message || '알 수 없는 오류'}`);
			return false;
		}
	}, [getPrinter]);

	const printWithPrinterName = useCallback(async (
		printerName: string,
		data: string,
		dataType: PrintDataType = 'html',
		labelSize?: { width: number; height: number },
	): Promise<boolean> => {
		if (!qz.websocket.isActive()) {
			alert('서버 프린터 설정이 연결되지 않았습니다.');
			return false;
		}
		try {
			const configOptions: any = {
				copies: 1, colorType: 'color', duplex: false,
				scaleContent: true, rasterize: false,
			};
			if (labelSize) {
				configOptions.units = 'mm';
				configOptions.margins = { top: 0, right: 0, bottom: 0, left: 0 };
				configOptions.orientation = 'portrait';
				configOptions.scaleContent = false;
				configOptions.density = 203;
			}
			const printConfig = qz.configs.create(printerName, configOptions);
			const printData: any[] = [{ type: 'pixel', format: 'html', flavor: 'plain', data }];
			await qz.print(printConfig, printData);
			return true;
		} catch (err: any) {
			console.error('직접 출력 실패:', err);
			alert(`출력 실패: ${err?.message}`);
			return false;
		}
	}, []);

	const printLabel = useCallback(async (
		lotNo: string,
		docType: PrintDocType = 'label1',
	): Promise<boolean> => {
		if (shouldSkipLabelPrintInDev()) return true;

		if (!qz.websocket.isActive()) {
			const win = window.open(`${config.apiUrl}/label/print/${lotNo}`, '_blank');
			win?.addEventListener('load', () => { win.print(); });
			return true;
		}

		try {
			const response = await fetch(`${config.apiUrl}/label/print/${lotNo}`);
			if (!response.ok) throw new Error(`HTTP ${response.status}`);
			const html = await response.text();
			return print({
				docType,
				data: html,
				dataType: 'html',
				labelSize: getLabelSize(docType as 'label1' | 'label2'),
			});
		} catch (err: any) {
			console.error('라벨 HTML 조회 실패:', err);
			alert(`라벨 데이터 조회 실패: ${err?.message}`);
			return false;
		}
	}, [print, getLabelSize]);

	const printBulkLabel = useCallback(async (
		labelDataList: LabelPrintData[],
		docType: PrintDocType = 'label1',
	): Promise<boolean> => {
		if (shouldSkipLabelPrintInDev()) return true;
		if (!labelDataList || labelDataList.length === 0) {
			alert('출력할 라벨 데이터가 없습니다.');
			return false;
		}

		const useBrowserPrint =
			!getQzConnected() ||
			!qz.websocket.isActive() ||
			((docType === 'label1' || docType === 'label2') && !getPrinter(docType));

		if (useBrowserPrint) {
			try {
				await printBulkLabelsAtOnce(labelDataList);
				return true;
			} catch (err: any) {
				console.error('일괄 라벨 출력 실패:', err);
				alert(`일괄 출력 실패: ${err?.message}`);
				return false;
			}
		}

		for (let i = 0; i < labelDataList.length; i++) {
			try {
				const response = await fetch(`${config.apiUrl}/label/print/bulk`, {
					method: 'POST',
					headers: { 'Content-Type': 'application/json' },
					body: JSON.stringify({ labels: [labelDataList[i]] }),
				});
				if (!response.ok) throw new Error(`HTTP ${response.status}`);
				const html = await response.text();
				const ok = await print({
					docType,
					data: html,
					dataType: 'html',
					labelSize: getLabelSize(docType as 'label1' | 'label2'),
					paperSize: isMediumLabel ? { width: 80, height: 60 } : undefined,
				});
				if (!ok) return false;
			} catch (err: any) {
				console.error(`라벨 ${i + 1} 출력 실패, 브라우저 인쇄로 대체:`, err);
				try {
					await printBulkLabelsAtOnce(labelDataList);
					alert('서버 프린터 설정 출력에 실패하여 브라우저 인쇄로 대체했습니다.');
					return true;
				} catch (fallbackErr: any) {
					alert(`라벨 출력 실패: ${err?.message ?? fallbackErr?.message}`);
					return false;
				}
			}
		}
		return true;
	}, [print, getLabelSize, getPrinter, isMediumLabel]);

	/**
	 * 설비 라벨 일괄 출력 (MST4010, MST4030 공용)
	 * QZ 연결: POST /label/print/bulk/equipment → label-equipment.html
	 * 미연결: printEquipmentBulkLabelsAtOnce → POST /label/print/browser/equipment
	 */
	const printBulkEquipmentLabel = useCallback(async (
		dataList: EquipmentLabelPrintData[],
		docType: PrintDocType = 'label1',
	): Promise<boolean> => {
		if (shouldSkipLabelPrintInDev()) return true;
		if (!dataList || dataList.length === 0) {
			alert('출력할 설비 라벨 데이터가 없습니다.');
			return false;
		}

		const useBrowserPrint =
			!getQzConnected() ||
			!qz.websocket.isActive() ||
			((docType === 'label1' || docType === 'label2') && !getPrinter(docType));

		if (useBrowserPrint) {
			try {
				await printEquipmentBulkLabelsAtOnce(dataList);
				return true;
			} catch (err: any) {
				console.error('설비 라벨 일괄 출력 실패:', err);
				alert(`설비 라벨 출력 실패: ${err?.message}`);
				return false;
			}
		}

		for (let i = 0; i < dataList.length; i++) {
			try {
				const response = await fetch(`${config.apiUrl}/label/print/bulk/equipment`, {
					method: 'POST',
					headers: { 'Content-Type': 'application/json' },
					body: JSON.stringify({ labels: [dataList[i]] }),
				});
				if (!response.ok) throw new Error(`HTTP ${response.status}`);
				const html = await response.text();
				const ok = await print({ docType, data: html, dataType: 'html', labelSize: getLabelSize(docType as 'label1' | 'label2') });
				if (!ok) return false;
			} catch (err: any) {
				console.error(`설비 라벨 ${i + 1} 출력 실패, 브라우저 인쇄로 대체:`, err);
				try {
					await printEquipmentBulkLabelsAtOnce(dataList);
					alert('QZ 출력 실패로 브라우저 인쇄로 대체했습니다.');
					return true;
				} catch (fallbackErr: any) {
					alert(`설비 라벨 출력 실패: ${err?.message ?? fallbackErr?.message}`);
					return false;
				}
			}
		}
		return true;
	}, [print, getLabelSize, getPrinter]);

	/**
	 * 치공구 라벨 일괄 출력 (MST4030 전용)
	 * QZ 연결: POST /label/print/bulk/tooling -> label-tooling.html
	 * 미연결: printToolingBulkLabelsAtOnce -> POST /label/print/browser/tooling
	 */
	const printBulkToolingLabel = useCallback(async (
		dataList: EquipmentLabelPrintData[],
		docType: PrintDocType = 'label1',
	): Promise<boolean> => {
		if (shouldSkipLabelPrintInDev()) return true;
		if (!dataList || dataList.length === 0) {
			alert('출력할 치공구 라벨 데이터가 없습니다.');
			return false;
		}

		const useBrowserPrint =
			!getQzConnected() ||
			!qz.websocket.isActive() ||
			((docType === 'label1' || docType === 'label2') && !getPrinter(docType));

		if (useBrowserPrint) {
			try {
				await printToolingBulkLabelsAtOnce(dataList);
				return true;
			} catch (err: any) {
				console.error('치공구 라벨 출력 실패:', err);
				alert(`치공구 라벨 출력 실패: ${err?.message}`);
				return false;
			}
		}

		for (let i = 0; i < dataList.length; i++) {
			try {
				const response = await fetch(`${config.apiUrl}/label/print/bulk/tooling`, {
					method: 'POST',
					headers: { 'Content-Type': 'application/json' },
					body: JSON.stringify({ labels: [dataList[i]] }),
				});
				if (!response.ok) throw new Error(`HTTP ${response.status}`);
				const html = await response.text();
				const ok = await print({ docType, data: html, dataType: 'html', labelSize: getLabelSize(docType as 'label1' | 'label2') });
				if (!ok) return false;
			} catch (err: any) {
				console.error(`치공구 라벨 ${i + 1} 출력 실패, 브라우저 인쇄로 대체:`, err);
				try {
					await printToolingBulkLabelsAtOnce(dataList);
					alert('QZ 출력 실패로 브라우저 인쇄로 대체했습니다.');
					return true;
				} catch (fallbackErr: any) {
					alert(`치공구 라벨 출력 실패: ${err?.message ?? fallbackErr?.message}`);
					return false;
				}
			}
		}
		return true;
	}, [print, getLabelSize, getPrinter]);

	const printPdfFromBlob = useCallback(async (
		blob: Blob,
		docType: PrintDocType = 'report1',
	): Promise<boolean> => {
		const base64 = await new Promise<string>((resolve, reject) => {
			const reader = new FileReader();
			reader.onloadend = () => {
				const result = reader.result as string;
				resolve(result?.split(',')[1] ?? '');
			};
			reader.onerror = () => reject(new Error('PDF base64 변환 실패'));
			reader.readAsDataURL(blob);
		});
		return print({ docType, data: base64, dataType: 'pdf' });
	}, [print]);

	return {
		print,
		printWithPrinterName,
		printLabel,
		printBulkLabel,
		printBulkEquipmentLabel,
		printBulkToolingLabel,
		printPdfFromBlob,
	};
};
